import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img src="/cubemate-logo.png" alt="Cubemate Logo" className="w-8 h-8 rounded-lg" />
            <span className="text-lg font-semibold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
              Cubemate
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-gray-400 mb-10">Last updated: March 17, 2026</p>

        <p className="text-gray-300 mb-8 leading-relaxed">
          At Corestrat AI Solutions ("we", "us", "our"), we are committed to protecting
          your privacy. This Privacy Policy explains how we collect, use, and safeguard
          your information when you use Cubemate (<a href="https://cubemate.ai" className="text-purple-400 hover:text-purple-300 underline">cubemate.ai</a>),
          our AI-powered learning platform.
        </p>

        {/* Section 1 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">1. Information We Collect</h2>
          <div className="space-y-4 text-gray-300 leading-relaxed">
            <div>
              <h3 className="text-lg font-medium text-gray-100 mb-1">Account Information</h3>
              <p>
                When you sign in using Google authentication (via Firebase), we receive your
                name, email address, and profile picture. We do not store your Google password.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-100 mb-1">Usage Data</h3>
              <p>
                We collect information about how you interact with the platform, including
                pages visited, features used, session duration, and the types of requests you make.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-100 mb-1">Code Submissions</h3>
              <p>
                Code you submit for execution is processed by our backend services. See
                Section 5 for details on how code submissions are handled.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-100 mb-1">Analytics</h3>
              <p>
                We use analytics services to understand usage patterns and improve the platform.
                This may include device type, browser information, and general location data.
              </p>
            </div>
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">2. How We Use Your Information</h2>
          <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed">
            <li>Provide, maintain, and improve the Cubemate platform</li>
            <li>Authenticate your identity and manage your account</li>
            <li>Process code execution requests and deliver results</li>
            <li>Track credit usage and manage subscription tiers</li>
            <li>Communicate important updates, changes, or support responses</li>
            <li>Analyze usage patterns to improve features and user experience</li>
            <li>Prevent abuse and ensure platform security</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">3. Third-Party Services</h2>
          <p className="text-gray-300 mb-4 leading-relaxed">
            We rely on trusted third-party providers to operate the platform. These services
            may process your data in accordance with their own privacy policies:
          </p>
          <div className="space-y-3 text-gray-300">
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
              <span className="font-medium text-gray-100">Firebase (Google)</span> — Authentication and user identity management
            </div>
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
              <span className="font-medium text-gray-100">Microsoft Azure</span> — Cloud hosting, database, AI services, and code execution infrastructure
            </div>
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
              <span className="font-medium text-gray-100">Razorpay</span> — Payment processing for users in India
            </div>
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
              <span className="font-medium text-gray-100">Stripe</span> — Payment processing for international users
            </div>
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
              <span className="font-medium text-gray-100">Google Analytics / Google Ads</span> — Website analytics and advertising attribution
            </div>
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
              <span className="font-medium text-gray-100">Meta Pixel</span> — Analytics and advertising attribution
            </div>
            <div className="bg-gray-900 rounded-lg p-4 border border-gray-800">
              <span className="font-medium text-gray-100">ipapi.co</span> — Geographic location detection for regional pricing
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">4. Data Storage</h2>
          <p className="text-gray-300 leading-relaxed">
            Your data is stored on Microsoft Azure cloud infrastructure. Our primary data
            center is located in the South India region. We use industry-standard encryption
            and security practices to protect your data both in transit and at rest.
          </p>
        </section>

        {/* Section 5 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">5. Code Execution</h2>
          <p className="text-gray-300 leading-relaxed">
            Code you submit through the platform is executed in isolated, sandboxed containers.
            These containers are ephemeral — they are created for each execution and destroyed
            afterward. Your code is not stored permanently after execution. We do not share
            your submitted code with other users or third parties.
          </p>
        </section>

        {/* Section 6 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">6. Cookies and Local Storage</h2>
          <p className="text-gray-300 leading-relaxed">
            We use cookies and browser local storage for the following purposes:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 mt-3 leading-relaxed">
            <li>Firebase authentication tokens to keep you signed in</li>
            <li>Local storage for your theme preferences and session data</li>
            <li>Analytics cookies from Google Analytics and Meta Pixel</li>
          </ul>
          <p className="text-gray-300 mt-3 leading-relaxed">
            You can manage cookie preferences through your browser settings, though disabling
            certain cookies may affect platform functionality.
          </p>
        </section>

        {/* Section 7 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">7. Data Retention</h2>
          <p className="text-gray-300 leading-relaxed">
            Your account data, including your profile information, conversation history,
            and saved tutorials, is retained for as long as your account remains active.
            If you choose to delete your account, we will remove your personal data within
            30 days, except where retention is required by law.
          </p>
        </section>

        {/* Section 8 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">8. Your Rights</h2>
          <p className="text-gray-300 mb-3 leading-relaxed">
            You have the following rights regarding your personal data:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-300 leading-relaxed">
            <li><span className="font-medium text-gray-100">Access</span> — Request a copy of the personal data we hold about you</li>
            <li><span className="font-medium text-gray-100">Correction</span> — Request correction of inaccurate data</li>
            <li><span className="font-medium text-gray-100">Deletion</span> — Request deletion of your account and associated data</li>
            <li><span className="font-medium text-gray-100">Export</span> — Request an export of your data in a portable format</li>
          </ul>
          <p className="text-gray-300 mt-3 leading-relaxed">
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:support@cubemate.ai" className="text-purple-400 hover:text-purple-300 underline">
              support@cubemate.ai
            </a>.
          </p>
        </section>

        {/* Section 9 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">9. Children's Privacy</h2>
          <p className="text-gray-300 leading-relaxed">
            Cubemate is not directed at children under the age of 13. We do not knowingly
            collect personal information from children under 13. If you believe a child
            under 13 has provided us with personal data, please contact us and we will
            take steps to remove that information.
          </p>
        </section>

        {/* Section 10 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">10. Changes to This Policy</h2>
          <p className="text-gray-300 leading-relaxed">
            We may update this Privacy Policy from time to time to reflect changes in our
            practices or for legal reasons. When we make significant changes, we will notify
            you via the email address associated with your account. We encourage you to review
            this page periodically.
          </p>
        </section>

        {/* Section 11 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4 text-white">11. Contact Us</h2>
          <p className="text-gray-300 leading-relaxed">
            If you have questions about this Privacy Policy or how we handle your data,
            please reach out to us:
          </p>
          <div className="mt-4 bg-gray-900 rounded-lg p-6 border border-gray-800">
            <p className="text-gray-100 font-medium">Corestrat AI Solutions</p>
            <p className="text-gray-300 mt-1">
              Email:{' '}
              <a href="mailto:support@cubemate.ai" className="text-purple-400 hover:text-purple-300 underline">
                support@cubemate.ai
              </a>
            </p>
            <p className="text-gray-300 mt-1">
              Website:{' '}
              <a href="https://cubemate.ai" className="text-purple-400 hover:text-purple-300 underline">
                cubemate.ai
              </a>
            </p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
          &copy; 2026 Corestrat AI Solutions. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
